import type { ComponentType } from "react";
import { routes } from "routing";
import { lazyView, type RouteRender } from "routing/tree";
import i18n from "i18n/i18next";

const { chat } = routes;

/**
 * Loads a room screen together with its ChatBodyProvider wrapper so both land in the same
 * chunk.
 */
const withChatBodyLazy = (load: () => Promise<{ default: ComponentType }>) => () =>
  Promise.all([import("feat/rocketchat/chatBody/chatBodyWrapper"), load()]).then(
    ([{ withChatBody }, { default: Page }]) => ({ default: withChatBody(Page) }),
  );

/** The chat tab root. Rendered under HomeRoutes in routes.tsx, same as home and team. */
export const chatRootRoutes: RouteRender[] = [
  lazyView(chat.list, () => import("./chat").then((m) => ({ default: m.ChatPage })), "Chat"),
];

/**
 * The room, which runs on the chat component library, and its info/members/notifications/
 * manage screens, each wrapped in ChatBodyProvider via withChatBody. Titles are ""
 * (not a fixed string) wherever the in-page NavigationView title is itself conditional
 * (room name, or "Group"/"Chat" wording by room type), same precedent as
 * routes.team.member.
 */
export const chatSubRoutes: RouteRender[] = [
  lazyView(chat.room, () => import("./room/chat-room").then((m) => ({ default: m.ChatRoomPage })), ""),
  lazyView(
    chat.info,
    withChatBodyLazy(() => import("./room/info/chat-room-info").then((m) => ({ default: m.ChatRoomInfoPage }))),
    "",
  ),
  lazyView(
    chat.members,
    withChatBodyLazy(() =>
      import("./room/info/members/chat-room-info-members").then((m) => ({ default: m.ChatRoomInfoMembersPage })),
    ),
    "",
  ),
  lazyView(
    chat.notifications,
    withChatBodyLazy(() =>
      import("./room/info/notifications/chat-room-info-notifications").then((m) => ({
        default: m.ChatRoomInfoNotificationsPage,
      })),
    ),
    () => "Notification Settings",
  ),
  lazyView(
    chat.manage,
    withChatBodyLazy(() => import("./room/manage/chat-room-manage").then((m) => ({ default: m.ChatRoomManagePage }))),
    () => "Manage Group Chat",
  ),
  lazyView(
    chat.new,
    () => import("./new/chat-new").then((m) => ({ default: m.ChatNewPage })),
    () => i18n.t("rocketChat.createGroup"),
  ),
];

export const chatRoutes: RouteRender[] = [...chatRootRoutes, ...chatSubRoutes];
